import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import i18next from 'i18next';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import { Conversation } from '../../conversation.js';
import { Exam, type ExamState } from '../../exam.js';
import { ALL_SUBJECTS, normalizeSubject, type Subject } from '../../subjects.js';
import { linkExamToClassroom } from '../../utils/classroom.js';
import { syncTeacherClassFromExam } from '../../utils/teacher-classes.js';
import { nudgeExamJobs } from '../jobs/job-store.js';
import { SET_EXAM_METADATA_TASK_CLASS } from '../pending-tasks/set-exam-metadata.task.js';

const DEFAULT_LNG = 'de';

/** Resolve an i18n code from a stored conversation language, which may be a code ("de") or a label ("Deutsch"). */
function resolveLngCode(language?: string | null): string {
  const v = String(language ?? '').trim().toLowerCase();
  if (!v) return DEFAULT_LNG;
  const byValue = SUPPORTED_LANGUAGES.find(l => l.value === v);
  if (byValue) return byValue.value;
  const byLabel = SUPPORTED_LANGUAGES.find(l => l.label.toLowerCase() === v);
  return byLabel ? byLabel.value : DEFAULT_LNG;
}

/** Localized display label for a subject key (used to build a human-readable exam title). */
function localizedSubject(subjectKey: Subject, lng: string): string {
  return String(i18next.t(`Subjects.${subjectKey}`, { lng, defaultValue: subjectKey }));
}

export const metadataToolDef = [
  {
    type: 'function',
    function: {
      name: 'set_metadata',
      description:
        'Setzt die Klausurmetadaten (Fach, Thema, optional Klasse) auf Basis des PENDING TASK "SetExamMetadataPendingTask". Rufe dieses Tool sofort mit fach/thema auf, wenn ein solcher Pending Task im Kontext vorliegt. Falls der Pending Task auch noch die Klasse erfragt, rufe es ein zweites Mal mit klasse auf, SOBALD der Nutzer auf die bereits gestellte Klassenfrage geantwortet hat — nicht vorher.',
      parameters: {
        type: 'object',
        properties: {
          fach: {
            type: 'string',
            enum: [...ALL_SUBJECTS],
            description:
              'Standardisierter Fach-Key der Klausur — exakt einer der erlaubten Enum-Werte (z.B. "math", "physics"). Nutze den Key aus dem Pending Task; falls keiner passt, "other".',
          },
          thema: { type: 'string', description: 'Thema der Klausur' },
          klasse: {
            type: 'string',
            description:
              'Vollständige Klassenbezeichnung, z.B. "9a", "10b", "Q1", "LK 12", "GK 11" — NUR angeben, nachdem der Nutzer auf die bereits gestellte Klassenfrage geantwortet hat. Kombiniere die Antwort mit bereits bekannten Teilen (Stufe/Kurs) zu einer vollständigen Bezeichnung.',
          },
        },
        required: ['fach', 'thema'],
      },
    },
  },
];

/**
 * The part of the tool result that tells the teacher where their exam's class ended up. Spelled out
 * per outcome (and as an explicit instruction to report it) because "Klasse gespeichert" reads like
 * success for all three, including the one where the exam was never attached to a classroom.
 */
function buildClassStatusLine(
  classLinked: 'matched' | 'created' | 'unresolved' | undefined,
  resolvedClass: string | undefined,
  joinCode: string | undefined,
  classStillNeeded: boolean,
): string {
  if (classStillNeeded) return 'Die Klasse ist noch offen und wird separat erfragt — erwähne sie nicht.';

  if (classLinked === 'created') {
    return `Die Klasse "${resolvedClass}" existierte noch NICHT und wurde neu angelegt${joinCode ? ` (Beitrittscode: ${joinCode})` : ''}; die Klausur wurde ihr erfolgreich zugeordnet und ist in der Seitenleiste unter "Klassen" zu finden. Teile dem Nutzer beides ausdrücklich mit: dass die Klasse neu erstellt wurde und dass die Klausur jetzt darin liegt.`;
  }
  if (classLinked === 'matched') {
    return `Die Klasse "${resolvedClass}" existierte BEREITS und wurde wiederverwendet; die Klausur wurde ihr erfolgreich zugeordnet. Teile dem Nutzer beides ausdrücklich mit: dass die Klasse schon vorhanden war und dass die Klausur jetzt darin liegt.`;
  }
  if (classLinked === 'unresolved') {
    return `ACHTUNG: Die Klassenbezeichnung "${resolvedClass}" wurde zwar an der Klausur gespeichert, konnte aber KEINER verwalteten Klasse zugeordnet werden — die Klausur taucht dort also nicht auf. Sage dem Nutzer ausdrücklich, dass die Zuordnung fehlgeschlagen ist und er die Klasse in den Klausur-Details setzen kann.`;
  }
  return 'Eine Klasse war für diese Klausur nicht zu setzen — erwähne sie nicht.';
}

export async function handleMetadata(exam: Exam, toolCallArgs: any): Promise<object> {
  const examId = exam._id;
  if (!examId) return { content: 'Fehler: Keine Klausur-ID vorhanden.' };

  // Coerce to a canonical subject key so only enum values reach storage; `fachLabel` is the
  // localized display form used in the human-readable title and user-facing messages.
  const fach = normalizeSubject(toolCallArgs?.fach);
  const thema = String(toolCallArgs?.thema ?? '').trim();
  const klasseRaw = typeof toolCallArgs?.klasse === 'string' ? toolCallArgs.klasse.trim() : '';

  if (!thema) {
    return { content: 'Fehler: Fach und Thema müssen angegeben werden.' };
  }

  try {
    const examCollection = await getCollection(Exam as any);

    // Localize the subject for the title using the conversation language (falls back to German).
    let lng = DEFAULT_LNG;
    try {
      if (exam.conversationId) {
        const conversation = await (await getCollection(Conversation)).findOne({ _id: exam.conversationId });
        lng = resolveLngCode((conversation as any)?.language);
      }
    } catch (langErr) {
      logger.warn({ err: langErr, examId, context: 'SetMetadata' }, 'Failed to resolve conversation language');
    }
    const fachLabel = localizedSubject(fach, lng);

    const freshExam = await examCollection.findOne({ _id: examId } as any);
    const pendingTasks: any[] = Array.isArray((freshExam as any)?.examState?.pendingTasks)
      ? (freshExam as any).examState.pendingTasks
      : [];
    const task = pendingTasks.find(t => t?._class === SET_EXAM_METADATA_TASK_CLASS);
    const classStillOpen = task ? task.classResolved === false : false;

    const existingClass = typeof (freshExam as any)?.class === 'string' ? (freshExam as any).class.trim() : '';

    // `linkExamToClassroom` normalizes the raw answer itself (splitting a combined label like
    // "LK 12 Sozialkunde" or "8c Mathe" into grade/suffix/subject), so the raw text is passed straight
    // through rather than pre-parsed here.
    let resolvedClass: string | undefined;
    let classRoomID: string | undefined;
    let classLinked: 'matched' | 'created' | 'unresolved' | undefined;
    let classJoinCode: string | undefined;
    if (klasseRaw) {
      // The teacher just answered the previously-posted class question (see exam-class-question.ts).
      const linkResult = await linkExamToClassroom(examId, klasseRaw, fach);
      resolvedClass = linkResult.resolvedClass;
      classRoomID = linkResult.classRoomID;
      classLinked = linkResult.linked;
      classJoinCode = linkResult.code;
    } else if (existingClass) {
      // No new class answer this call, but a class was already linked earlier (e.g. the structure step
      // linked it immediately from a fully-labeled sheet). Re-resolve it for the now-current subject so
      // `Exam.class`/`Exam.subject` never drift apart.
      const linkResult = await linkExamToClassroom(examId, existingClass, fach);
      resolvedClass = linkResult.resolvedClass;
      classRoomID = linkResult.classRoomID;
      classLinked = linkResult.linked;
      classJoinCode = linkResult.code;
    }

    const updateFields: Record<string, unknown> = {
      subject: fach,
      topic: thema,
      title: `${fachLabel} - ${thema}`,
      'examState.metaDataSet': true,
    };
    if (resolvedClass) {
      updateFields.class = resolvedClass;
      updateFields.classRoomID = classRoomID ?? null;
    }

    await examCollection.updateOne({ _id: examId } as any, { $set: updateFields });

    if (resolvedClass) {
      await syncTeacherClassFromExam(examId, resolvedClass);
    }

    // The task (and its "set_metadata" availability) only goes away once the class part is resolved
    // too — if the pending task is still waiting on the class answer and this call didn't supply one,
    // leave it in place so the tool stays available for the follow-up call.
    const classStillNeeded = classStillOpen && !klasseRaw;
    if (!classStillNeeded) {
      await examCollection.updateOne({ _id: examId } as any, {
        $pull: { 'examState.pendingTasks': { _class: SET_EXAM_METADATA_TASK_CLASS } } as any,
      });
    }

    // WAKES WHAT THE CLASS GATE WAS HOLDING (see `examClassPending` in jobs/job-conditions.ts): the
    // master-solution, grading-scheme and answer extractions are deferred rows with a `runAfter` up to
    // WAIT_BACKOFF_MS away, so without this the answer the teacher just gave takes up to 30 seconds to
    // restart the pipeline. The $pull above IS the release; this only makes it prompt — same reasoning
    // as `openStructureGate`'s nudge, and likewise last, after the state it re-reads is committed.
    if (classStillOpen && !classStillNeeded) {
      await nudgeExamJobs(examId);
    }

    logger.info(
      { examId, fach, fachLabel, thema, classLinked, klasse: resolvedClass, context: 'SetMetadata' },
      'Exam metadata set',
    );

    const metadataLine = `Fach ("${fachLabel}") und Thema ("${thema}") wurden für die Generierung von Musterlösung und Bewertungsschema gespeichert.`;

    // The teacher must be told BOTH facts explicitly — whether the class already existed or had to be
    // created, and whether this exam actually ended up in that classroom. `linked: 'unresolved'` means
    // the class label was stored on the exam but no classroom was matched or created for it, so the
    // exam will NOT appear under "Klausuren in dieser Klasse" — reporting that as success would be a
    // lie the teacher only discovers when the class section stays empty.
    return { content: `${metadataLine} ${buildClassStatusLine(classLinked, resolvedClass, classJoinCode, classStillNeeded)}` };
  } catch (err) {
    logger.error({ err, examId, context: 'SetMetadata' }, 'Failed for exam');
    return { content: 'Fehler beim Setzen der Metadaten.' };
  }
}

export function metadataAvailability(examState: ExamState): boolean {
  return (
    Array.isArray(examState?.pendingTasks) &&
    examState.pendingTasks.some((t: any) => t?._class === SET_EXAM_METADATA_TASK_CLASS)
  );
}
